import React, { useCallback, useMemo, useState } from 'react';
import { Autocomplete, CircularProgress, TextField, TextFieldProps } from '@mui/material';
import { useAsyncPaginatedCatalog } from '../../../hooks/useAsyncPaginatedCatalog';
import {
  fetchActiveSuppliersPage,
  fetchCategoriesPage,
  fetchColorsPage,
  fetchMaterialsPage,
  fetchSuppliersPage,
  fetchTagsPage,
} from '../../../utils/catalogPageFetchers';

export type CatalogAutocompleteType =
  | 'category'
  | 'material'
  | 'color'
  | 'supplier'
  | 'activeSupplier'
  | 'tag';

export interface CatalogOption {
  id: number;
  name: string;
  color?: string;
}

export interface CatalogAutocompleteProps {
  catalogType: CatalogAutocompleteType;
  value: string | number | null | undefined;
  onChange: (value: string, option: CatalogOption | null) => void;
  valueMode?: 'id' | 'name';
  label: string;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  error?: boolean;
  helperText?: React.ReactNode;
  noOptionsText?: string;
  pageSize?: number;
  debounceMs?: number;
  pinOptions?: CatalogOption[];
  isActive?: boolean;
  fullWidth?: boolean;
  size?: 'small' | 'medium';
  textFieldProps?: Omit<TextFieldProps, 'value' | 'onChange' | 'label' | 'placeholder'>;
}

const FETCHERS = {
  category: fetchCategoriesPage,
  material: fetchMaterialsPage,
  color: fetchColorsPage,
  supplier: fetchSuppliersPage,
  activeSupplier: fetchActiveSuppliersPage,
  tag: fetchTagsPage,
};

function hashName(value: string): number {
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash * 31 + value.charCodeAt(i)) | 0;
  }
  return -Math.max(1, Math.abs(hash));
}

function hasValue(value: string | number | null | undefined): value is string | number {
  return value !== undefined && value !== null && String(value).trim() !== '';
}

function normalizeSearch(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

export const CatalogAutocomplete: React.FC<CatalogAutocompleteProps> = ({
  catalogType,
  value,
  onChange,
  valueMode = 'id',
  label,
  placeholder,
  disabled = false,
  required = false,
  error = false,
  helperText,
  noOptionsText = 'Sin coincidencias',
  pageSize = 10,
  debounceMs = 350,
  pinOptions = [],
  isActive = true,
  fullWidth = true,
  size = 'medium',
  textFieldProps,
}) => {
  const selectedValue = hasValue(value) ? String(value) : '';
  const [filterText, setFilterText] = useState('');

  const pinItems = useMemo(() => {
    if (!selectedValue) return pinOptions;

    const hasPinnedValue = pinOptions.some((option) =>
      valueMode === 'id'
        ? String(option.id) === selectedValue
        : option.name.toLowerCase() === selectedValue.toLowerCase(),
    );

    if (hasPinnedValue) return pinOptions;

    const syntheticOption =
      valueMode === 'id'
        ? { id: Number(selectedValue), name: `#${selectedValue}` }
        : { id: hashName(selectedValue), name: selectedValue };

    return [syntheticOption, ...pinOptions];
  }, [pinOptions, selectedValue, valueMode]);

  const catalog = useAsyncPaginatedCatalog<CatalogOption>({
    isActive: isActive && !disabled,
    fetchPage: FETCHERS[catalogType],
    pageSize,
    debounceMs,
    pinItems,
  });

  const selectedOption = useMemo(() => {
    if (!selectedValue) return null;
    return (
      catalog.displayOptions.find((option) =>
        valueMode === 'id'
          ? String(option.id) === selectedValue
          : option.name.toLowerCase() === selectedValue.toLowerCase(),
      ) || null
    );
  }, [catalog.displayOptions, selectedValue, valueMode]);

  const filterOptions = useCallback(
    (options: CatalogOption[]) => {
      const normalizedFilter = normalizeSearch(filterText);
      if (!normalizedFilter) return catalog.filterOptions(options);

      return catalog
        .filterOptions(options)
        .filter((option) => normalizeSearch(option.name).includes(normalizedFilter));
    },
    [catalog, filterText],
  );

  return (
    <Autocomplete
      options={catalog.displayOptions}
      filterOptions={filterOptions}
      loading={catalog.loading}
      openOnFocus
      onOpen={catalog.onOpen}
      onInputChange={(event, newInputValue, reason) => {
        if (reason === 'input') {
          setFilterText(newInputValue);
        } else if (reason === 'clear' || reason === 'reset') {
          setFilterText('');
        }
        catalog.onInputChange(event, newInputValue, reason);
      }}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option, selected) => option.id === selected.id}
      value={selectedOption}
      onChange={(_, newValue) => {
        if (!newValue) {
          setFilterText('');
          onChange('', null);
          return;
        }
        setFilterText('');
        onChange(valueMode === 'id' ? String(newValue.id) : newValue.name, newValue);
      }}
      disabled={disabled}
      fullWidth={fullWidth}
      size={size}
      ListboxProps={catalog.listboxProps}
      noOptionsText={noOptionsText}
      renderInput={(params) => (
        <TextField
          {...params}
          {...textFieldProps}
          label={label}
          placeholder={placeholder}
          required={required}
          error={error}
          helperText={helperText}
          InputLabelProps={{ shrink: true, ...textFieldProps?.InputLabelProps }}
          inputProps={{
            ...params.inputProps,
            ...textFieldProps?.inputProps,
          }}
          InputProps={{
            ...params.InputProps,
            ...textFieldProps?.InputProps,
            endAdornment: (
              <>
                {catalog.loading ? <CircularProgress color="inherit" size={18} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default CatalogAutocomplete;
